import { createSelector } from '@reduxjs/toolkit';
import { AspectRatio } from 'core/constants/enums/AspectRatioEnum.ts';
import { GenerationStyleTabs } from 'core/constants/enums/GenerationStyleTabs.ts';
import { StudioSidebarViews } from 'core/constants/enums/StudioSidebarViews.ts';
import { APP, STUDIO, TEXT_TO_IMAGE } from 'core/navigation/Routes';
import { setIsTemplateAssetSelected } from 'core/store/reducers/assetsReducer/AssetsSlice';
import {
	canvasAIGuidanceInputSelector,
	canvasAspectRatioInputSelector,
	canvasBatchSizeInputSelector,
	canvasClothingInputImageSelector,
	canvasClothingTargetImageSelector,
	canvasClothingTypeValueSelector,
	canvasControlNetControlModeSelector,
	canvasControlNetEnabledSelector,
	canvasControlNetFluxTypeSelector,
	canvasControlNetImageSelector,
	canvasControlNetModelsApiStageSelector,
	canvasControlNetModelsSelector,
	canvasControlNetNonSDXLTypeSelector,
	canvasControlNetResizeModeSelector,
	canvasControlNetSDXLTypeSelector,
	canvasFacelockEnabledSelector,
	canvasFacelockImageSelector,
	canvasFaceReplaceCostAPIStageSelector,
	canvasFaceReplaceCostSelector,
	canvasFaceReplaceImageSelector,
	canvasFaceReplaceTargetImageIndexSelector,
	canvasFaceReplaceTargetImagesSelector,
	canvasFeedAPIStageSelector,
	canvasFluxModelsSelector,
	canvasFluxStylesSelector,
	canvasImageReferenceStyleSelector,
	canvasIntensityCountInputSelector,
	canvasIsTrueTouchImageUploadingSelector,
	canvasLightningModelsSelector,
	canvasMediaFeedbackModalSelector,
	canvasMediaToolsSidebarViewSelector,
	canvasMediaTypeSelector,
	canvasMusicVideoAspectRatioSelector,
	canvasMusicVideoAudioAssetDataSelector,
	canvasMusicVideoCharacterSelector,
	canvasMusicVideoTemplatesApiStageSelector,
	canvasMusicVideoTemplatesDataSelector,
	canvasMusicVideoThemeSelector,
	canvasNegativePromptInputSelector,
	canvasProductPhotoBackgroundImageSelector,
	canvasProductPhotoCompositeImageSelector,
	canvasProductPhotoImageSelector,
	canvasProductPhotoImageToVideoGuideToggleSelector,
	canvasProductPhotoModeSelector,
	canvasProductPhotoObjectLocationImageSelector,
	canvasProductPhotoReflectionSelector,
	canvasProductPhotoWithoutBackgroundImageApiStageSelector,
	canvasProductPhotoWithoutBackgroundImageSelector,
	canvasPromptInputSelector,
	canvasRandomiseSeedInputSelector,
	canvasSamplerInputSelector,
	canvasSamplingStepsInputSelector,
	canvasSeedInputSelector,
	canvasSelectedFluxModelsSelector,
	canvasSelectedFluxStylesSelector,
	canvasSelectedLightningModelsSelector,
	canvasSelectedModelsSelector,
	canvasSelectedStylesSelector,
	canvasStyleCategoriesApiStageSelector,
	canvasStyleCategoriesSelector,
	canvasStylesApiStageSelector,
	canvasStylesSelector,
	canvasTrueTouchImageSelector,
	canvasTrueTouchRealismEffectSelector,
	canvasTrueTouchScaleFactorSelector,
	canvasUpscaleImageSelector,
	canvasUpscaleOutputResolutionSelector,
	canvasVideoAnyoneDurationSelector,
	canvasVideoAnyoneEndFrameImageSelector,
	canvasVideoAnyoneImageSelector,
	canvasVideoAnyoneModelNameSelector,
	canvasVideoAnyonePromptSelector,
	canvasVideoFaceSwapImageSelector,
	canvasVideoFaceSwapRenderingPrefSelector,
	canvasVideoFaceSwapVideoSelector,
	currentGenerationStyleTabSelector,
	generateCanvasImageAPIStageSelector,
	getCanvasFaceReplaceCostAsync,
	getModelsAPIStageSelector as getCanvasModelsAPIStageSelector,
	handleCanvasProductPhotoAssetFromPusher,
	lipSyncAudioAssetDataSelector as canvasLipSyncAudioAssetDataSelector,
	lipSyncImageAssetDataSelector as canvasLipSyncImageAssetDataSelector,
	lipSyncIsFreeVideoUsedSelector as canvasLipSyncIsFreeVideoUsedSelector,
	lipSyncScriptSelector as canvasLipSyncScriptSelector,
	lipSyncVideoAssetDataSelector as canvasLipSyncVideoAssetDataSelector,
	lipSyncVoiceDataSelector as canvasLipSyncVoiceDataSelector,
	modelsSelector as canvasModelsSelector,
	productPhotoCanvasRemoveImageBackgroundAsync,
	randomiseCanvasPromptAPIStageSelector,
	randomiseCanvasPromptAsync,
	removeCanvasFaceReplaceTargetImage,
	resetCanvasClothingInputImage,
	resetCanvasClothingTargetImage,
	resetCanvasControlNetImage,
	resetCanvasFacelockImage,
	resetCanvasFaceReplaceImage,
	resetCanvasFaceReplaceTargetImages,
	resetCanvasImageReferenceStyle,
	resetCanvasLipSyncAudioAssetData,
	resetCanvasLipSyncImageAssetData,
	resetCanvasLipSyncVideoAssetData,
	resetCanvasMusicVideoAudioAssetData,
	resetCanvasProductPhotoBackgroundImage,
	resetCanvasProductPhotoImage,
	resetCanvasTrueTouchImage,
	resetCanvasUpscaleImage,
	resetCanvasVideoAnyoneEndFrameImage,
	resetCanvasVideoAnyoneImage,
	setCanvasAIGuidanceInput,
	setCanvasAppBarMediaType,
	setCanvasAspectRatioInput,
	setCanvasBatchSizeInput,
	setCanvasClothingInputImage,
	setCanvasClothingTargetImage,
	setCanvasClothingTypeValue,
	setCanvasControlNetControlMode,
	setCanvasControlNetEnabled,
	setCanvasControlNetFluxType,
	setCanvasControlNetNonSDXLType,
	setCanvasControlNetResizeMode,
	setCanvasControlNetSDXLType,
	setCanvasFacelockEnabled,
	setCanvasFaceReplaceImage,
	setCanvasFaceReplaceImageId,
	setCanvasFaceReplaceTargetImageIndex,
	setCanvasFaceReplaceTargetImages,
	setCanvasImageReferenceStyle,
	setCanvasIntensityCountInput,
	setCanvasIsLipSyncFreeVideoUsed,
	setCanvasLipSyncAudioAssetData,
	setCanvasLipSyncImageAssetData,
	setCanvasLipSyncScript,
	setCanvasLipSyncVideoAssetData,
	setCanvasLipSyncVoiceData,
	setCanvasMediaToolsSidebarView,
	setCanvasMusicVideoAspectRatio,
	setCanvasMusicVideoAudioAssetData,
	setCanvasMusicVideoCharacter,
	setCanvasMusicVideoTheme,
	setCanvasNegativePromptInput,
	setCanvasProductPhotoBackgroundImage,
	setCanvasProductPhotoCompositeImage,
	setCanvasProductPhotoImage,
	setCanvasProductPhotoMode,
	setCanvasProductPhotoObjectLocationImage,
	setCanvasProductPhotoObjectTransformValues,
	setCanvasProductPhotoReflection,
	setCanvasProductPhotoWithoutBackgroundImage,
	setCanvasPromptInput,
	setCanvasRandomiseSeedInput,
	setCanvasSamplerInput,
	setCanvasSamplingStepsInput,
	setCanvasSeedInput,
	setCanvasSelectedFluxStyles,
	setCanvasTrueTouchRealismEffect,
	setCanvasTrueTouchScaleFactor,
	setCanvasUpscaleImage,
	setCanvasUpscaleOutputResolution,
	setCanvasVideoAnyoneDuration,
	setCanvasVideoAnyoneEndFrameImage,
	setCanvasVideoAnyoneImage,
	setCanvasVideoAnyoneImageId,
	setCanvasVideoAnyoneModelName,
	setCanvasVideoAnyoneModelProvider,
	setCanvasVideoAnyonePrompt,
	setCurrentGenerationStyleTab,
	toggleCanvasMediaFeedbackModal,
	toggleCanvasProductPhotoImageToVideoGuide,
	uploadCanvasControlNetImageFileAsync,
	uploadCanvasControlNetImageLinkAsync,
	uploadCanvasFaceLockImageFileAsync,
	uploadCanvasFaceLockImageLinkAsync,
	uploadCanvasTrueTouchImageFileAsync,
	uploadCanvasTrueTouchImageLinkAsync,
} from 'core/store/reducers/canvasReducer/CanvasSlice';
import {
	handleAddCanvasSelectedLora,
	handleSetCanvasSelectedFluxModel,
	handleSetCanvasSelectedLightningModel,
	handleSetCanvasSelectedModel,
	handleSetCanvasSelectedStyle,
} from 'core/store/reducers/canvasReducer/CanvasSliceThunk';
import {
	controlWeightHomeSelector,
	controlWeightSelector,
	controlWeightSelectorStudio,
	homeSelectedCharacterSelector,
	realisticFaceSwapHomeSelector,
	realisticFaceSwapSelector,
	realisticFaceSwapSelectorStudio,
	selectedCharacterSelector,
	setControlWeight,
	setControlWeightStudio,
	studioSelectedCharacterSelector,
	toggleEnableRealisticFaceSwap,
	toggleEnableRealisticFaceSwapStudio,
} from 'core/store/reducers/characterStudioReducer/CharaterStudioSlice';
import { setHomePageCharacterPromptInput } from 'core/store/reducers/homeReducer/HomeSlice';
import {
	generateStudioMediaAPIStageSelector,
	getModelsAPIStageSelector as getStudioModelsApiStageSelector,
	getStudioFaceReplaceCostAsync,
	getStudioFeedAPIStageSelector,
	handleStudioProductPhotoAssetFromPusher,
	lipSyncAudioAssetDataSelector as studioLipSyncAudioAssetDataSelector,
	lipSyncImageAssetDataSelector as studioLipSyncImageAssetDataSelector,
	lipSyncIsFreeVideoUsedSelector as studioLipSyncIsFreeVideoUsedSelector,
	lipSyncScriptSelector as studioLipSyncScriptSelector,
	lipSyncVideoAssetDataSelector as studioLipSyncVideoAssetDataSelector,
	lipSyncVoiceDataSelector as studioLipSyncVoiceDataSelector,
	modelsSelector as studioModelsSelector,
	productPhotoStudioRemoveImageBackgroundAsync,
	randomiseStudioPromptAPIStageSelector,
	randomiseStudioPromptAsync,
	removeStudioFaceReplaceTargetImage,
	resetStudioChangeAnythingImage,
	resetStudioClothingInputImage,
	resetStudioClothingTargetImage,
	resetStudioControlNetImage,
	resetStudioFacelockImage,
	resetStudioFaceReplaceImage,
	resetStudioFaceReplaceTargetImages,
	resetStudioImageReferenceStyle,
	resetStudioLipSyncAudioAssetData,
	resetStudioLipSyncImageAssetData,
	resetStudioLipSyncVideoAssetData,
	resetStudioMusicVideoAudioAssetData,
	resetStudioProductPhotoBackgroundImage,
	resetStudioProductPhotoImage,
	resetStudioTrueTouchImage,
	resetStudioUpscaleImage,
	resetStudioVideoAnyoneEndFrameImage,
	resetStudioVideoAnyoneImage,
	setStudioAIGuidanceInput,
	setStudioAppBarMediaType,
	setStudioAspectRatioInput,
	setStudioBatchSizeInput,
	setStudioClothingInputImage,
	setStudioClothingTargetImage,
	setStudioClothingTypeValue,
	setStudioControlNetControlMode,
	setStudioControlNetEnabled,
	setStudioControlNetFluxType,
	setStudioControlNetNonSDXLType,
	setStudioControlNetResizeMode,
	setStudioControlNetSDXLType,
	setStudioFacelockEnabled,
	setStudioFaceReplaceImage,
	setStudioFaceReplaceImageId,
	setStudioFaceReplaceTargetImageIndex,
	setStudioFaceReplaceTargetImages,
	setStudioGenerationSidebarView,
	setStudioImageReferenceStyle,
	setStudioIntensityCountInput,
	setStudioIsLipSyncFreeVideoUsed,
	setStudioLipSyncAudioAssetData,
	setStudioLipSyncImageAssetData,
	setStudioLipSyncScript,
	setStudioLipSyncVideoAssetData,
	setStudioLipSyncVoiceData,
	setStudioMusicVideoAspectRatio,
	setStudioMusicVideoAudioAssetData,
	setStudioMusicVideoCharacter,
	setStudioMusicVideoTheme,
	setStudioNegativePromptInput,
	setStudioProductPhotoBackgroundImage,
	setStudioProductPhotoCompositeImage,
	setStudioProductPhotoImage,
	setStudioProductPhotoMode,
	setStudioProductPhotoObjectLocationImage,
	setStudioProductPhotoObjectTransformValues,
	setStudioProductPhotoReflection,
	setStudioProductPhotoWithoutBackgroundImage,
	setStudioPromptInput,
	setStudioRandomiseSeedInput,
	setStudioSamplerInput,
	setStudioSamplingStepsInput,
	setStudioSeedInput,
	setStudioSelectedFluxModels,
	setStudioSelectedFluxStyles,
	setStudioSelectedLightningModels,
	setStudioSidebarView,
	setStudioTrueTouchRealismEffect,
	setStudioTrueTouchScaleFactor,
	setStudioUpscaleImage,
	setStudioUpscaleOutputResolution,
	setStudioVideoAnyoneDuration,
	setStudioVideoAnyoneEndFrameImage,
	setStudioVideoAnyoneImage,
	setStudioVideoAnyoneImageId,
	setStudioVideoAnyoneModelName,
	setStudioVideoAnyoneModelProvider,
	setStudioVideoAnyonePrompt,
	studioAIGuidanceInputSelector,
	studioAspectRatioInputSelector,
	studioBatchSizeInputSelector,
	studioChangeAnythingImageSelector,
	studioClothingInputImageSelector,
	studioClothingTargetImageSelector,
	studioClothingTypeValueSelector,
	studioControlNetControlModeSelector,
	studioControlNetEnabledSelector,
	studioControlNetFluxTypeSelector,
	studioControlNetImageSelector,
	studioControlNetModelsApiStageSelector,
	studioControlNetModelsSelector,
	studioControlNetNonSDXLTypeSelector,
	studioControlNetResizeModeSelector,
	studioControlNetSDXLTypeSelector,
	studioFacelockEnabledSelector,
	studioFacelockImageSelector,
	studioFaceReplaceCostAPIStageSelector,
	studioFaceReplaceCostSelector,
	studioFaceReplaceImageSelector,
	studioFaceReplaceTargetImageIndexSelector,
	studioFaceReplaceTargetImagesSelector,
	studioFluxModelsSelector,
	studioFluxStylesSelector,
	studioGenerationSidebarViewSelector,
	studioImageReferenceStyleSelector,
	studioIntensityCountInputSelector,
	studioIsTrueTouchImageUploadingSelector,
	studioLightningModelsSelector,
	studioMediaFeedbackModalSelector,
	studioMediaTypeSelector,
	studioMusicVideoAspectRatioSelector,
	studioMusicVideoAudioAssetDataSelector,
	studioMusicVideoCharacterSelector,
	studioMusicVideoTemplatesApiStageSelector,
	studioMusicVideoTemplatesDataSelector,
	studioMusicVideoThemeSelector,
	studioNegativePromptInputSelector,
	studioProductPhotoBackgroundImageSelector,
	studioProductPhotoCompositeImageSelector,
	studioProductPhotoImageSelector,
	studioProductPhotoImageToVideoGuideToggleSelector,
	studioProductPhotoModeSelector,
	studioProductPhotoObjectLocationImageSelector,
	studioProductPhotoReflectionSelector,
	studioProductPhotoWithoutBackgroundImageApiStageSelector,
	studioProductPhotoWithoutBackgroundImageSelector,
	studioPromptInputSelector,
	studioRandomiseSeedInputSelector,
	studioSamplerInputSelector,
	studioSamplingStepsInputSelector,
	studioSeedInputSelector,
	studioSelectedFluxModelsSelector,
	studioSelectedFluxStylesSelector,
	studioSelectedLightningModelsSelector,
	studioSelectedModelsSelector,
	studioSelectedStylesSelector,
	studioSidebarViewSelector,
	studioStyleCategoriesApiStageSelector,
	studioStyleCategoriesSelector,
	studioStylesApiStageSelector,
	studioStylesSelector,
	studioTrueTouchImageSelector,
	studioTrueTouchRealismEffectSelector,
	studioTrueTouchScaleFactorSelector,
	studioUpscaleImageSelector,
	studioUpscaleOutputResolutionSelector,
	studioVideoAnyoneDurationSelector,
	studioVideoAnyoneEndFrameImageSelector,
	studioVideoAnyoneImageSelector,
	studioVideoAnyoneModelNameSelector,
	studioVideoAnyonePromptSelector,
	studioVideoFaceSwapImageSelector,
	studioVideoFaceSwapRendingPrefSelector,
	studioVideoFaceSwapVideoSelector,
	toggleStudioMediaFeedbackModal,
	toggleStudioProductPhotoImageToVideoGuide,
	uploadStudioChangeAnythingImageFileAsync,
	uploadStudioChangeAnythingImageLinkAsync,
	uploadStudioControlNetImageFileAsync,
	uploadStudioControlNetImageLinkAsync,
	uploadStudioFaceLockImageFileAsync,
	uploadStudioFaceLockImageLinkAsync,
	uploadStudioTrueTouchImageFileAsync,
	uploadStudioTrueTouchImageLinkAsync,
} from 'core/store/reducers/studioReducer/StudioSlice';
import {
	handleSetStudioSelectedLora,
	handleSetStudioSelectedModel,
	handleSetStudioSelectedStyle,
} from 'core/store/reducers/studioReducer/StudioSliceThunk';

export const selectedModelsSelector = createSelector(
	[studioSelectedModelsSelector, canvasSelectedModelsSelector, (state, route) => route],
	(studioSelectedModels, canvasSelectedModels, route) => {
		if (route === TEXT_TO_IMAGE) return studioSelectedModels;
		if (route === STUDIO) return canvasSelectedModels;
		return [];
	},
);
export const handleSetSelectedModel = (model, route) => (dispatch) => {
	if (route === TEXT_TO_IMAGE) {
		dispatch(handleSetStudioSelectedModel(model));
	} else if (route === STUDIO) {
		dispatch(handleSetCanvasSelectedModel({ model }));
	}
};
export const getModelsAPIStageSelector = createSelector(
	[getStudioModelsApiStageSelector, getCanvasModelsAPIStageSelector, (state, route) => route],
	(studioModelsApiStageSelector, canvasModelsApiStageSelector, route) => {
		if (route === TEXT_TO_IMAGE) return studioModelsApiStageSelector;
		if (route === STUDIO) return canvasModelsApiStageSelector;
		return [];
	},
);

export const getModelSelector = createSelector(
	[studioModelsSelector, canvasModelsSelector, (state, route) => route],
	(studioModels, canvasModels, route) => {
		if (route === TEXT_TO_IMAGE) return studioModels;
		if (route === STUDIO) return canvasModels;
		return [];
	},
);
export const styleCategoriesApiStageSelector = createSelector(
	[
		studioStyleCategoriesApiStageSelector,
		canvasStyleCategoriesApiStageSelector,
		(state, route) => route,
	],
	(studioStyleCategoriesApiStage, canvasStyleCategoriesApiStage, route) => {
		if (route === TEXT_TO_IMAGE) return studioStyleCategoriesApiStage;
		if (route === STUDIO) return canvasStyleCategoriesApiStage;
		return [];
	},
);

export const styleCategoriesSelector = createSelector(
	[studioStyleCategoriesSelector, canvasStyleCategoriesSelector, (state, route) => route],
	(studioStyleCategories, canvasStyleCategories, route) => {
		if (route === TEXT_TO_IMAGE) return studioStyleCategories;
		if (route === STUDIO) return canvasStyleCategories;
		return [];
	},
);

export const stylesApiStageSelector = createSelector(
	[studioStylesApiStageSelector, canvasStylesApiStageSelector, (state, route) => route],
	(studioStylesApiStage, canvasStylesApiStage, route) => {
		if (route === TEXT_TO_IMAGE) return studioStylesApiStage;
		if (route === STUDIO) return canvasStylesApiStage;
		return [];
	},
);

export const stylesSelector = createSelector(
	[studioStylesSelector, canvasStylesSelector, (state, route) => route],
	(studioStyles, canvasStyles, route) => {
		if (route === TEXT_TO_IMAGE) return studioStyles;
		if (route === STUDIO) return canvasStyles;
		return [];
	},
);

export const selectedStylesSelector = createSelector(
	[studioSelectedStylesSelector, canvasSelectedStylesSelector, (state, route) => route],
	(studioSelectedStyles, canvasSelectedStyles, route) => {
		if (route === TEXT_TO_IMAGE) return studioSelectedStyles;
		if (route === STUDIO) return canvasSelectedStyles;
		return [];
	},
);

export const handleSelectedStyle = (styleId, route) => (dispatch) => {
	if (route === TEXT_TO_IMAGE) {
		dispatch(handleSetStudioSelectedStyle({ style: styleId }));
	} else if (route === STUDIO) {
		dispatch(handleSetCanvasSelectedStyle({ style: styleId }));
	}
};

export const fluxStylesSelector = createSelector(
	[studioFluxStylesSelector, canvasFluxStylesSelector, (state, route) => route],
	(studioFluxStyles, canvasFluxStyles, route) => {
		if (route === TEXT_TO_IMAGE) return studioFluxStyles;
		if (route === STUDIO) return canvasFluxStyles;
		return [];
	},
);

export const selectedFluxStylesSelector = createSelector(
	[studioSelectedFluxStylesSelector, canvasSelectedFluxStylesSelector, (state, route) => route],
	(studioSelectedFluxStyles, canvasSelectedFluxStyles, route) => {
		if (route === TEXT_TO_IMAGE) return studioSelectedFluxStyles;
		if (route === STUDIO) return canvasSelectedFluxStyles;
		return [];
	},
);

export const handleSelectedFluxStyle = (styleId, route) => (dispatch) => {
	if (route === TEXT_TO_IMAGE) {
		dispatch(setStudioSelectedFluxStyles(styleId));
	} else if (route === STUDIO) {
		dispatch(setCanvasSelectedFluxStyles(styleId));
	}
};

export const promptInputSelector = createSelector(
	[studioPromptInputSelector, canvasPromptInputSelector, (state, route) => route],
	(studioPromptInput, canvasPromptInput, route) => {
		if (route === TEXT_TO_IMAGE) return studioPromptInput;
		if (route === STUDIO) return canvasPromptInput;
		return [];
	},
);
export const setPromptInput = (value, route) => (dispatch) => {
	if (route === TEXT_TO_IMAGE) {
		dispatch(setStudioPromptInput(value));
	} else if (route === APP) {
		dispatch(setHomePageCharacterPromptInput(value));
	} else if (route === STUDIO) {
		dispatch(setCanvasPromptInput(value));
	}
};

export const negativePromptInputSelector = createSelector(
	[studioNegativePromptInputSelector, canvasNegativePromptInputSelector, (state, route) => route],
	(studioNegativePromptInput, canvasNegativePromptInput, route) => {
		if (route === TEXT_TO_IMAGE) return studioNegativePromptInput;
		if (route === STUDIO) return canvasNegativePromptInput;
		return [];
	},
);
export const setNegativePromptInput = (value, route) => (dispatch) => {
	if (route === TEXT_TO_IMAGE) {
		dispatch(setStudioNegativePromptInput(value));
	} else if (route === STUDIO) {
		dispatch(setCanvasNegativePromptInput(value));
	}
};

export const seedInputSelector = createSelector(
	[studioSeedInputSelector, canvasSeedInputSelector, (state, route) => route],
	(studioSeedInput, canvasSeedInput, route) => {
		if (route === TEXT_TO_IMAGE) return studioSeedInput;
		if (route === STUDIO) return canvasSeedInput;
		return [];
	},
);
export const setSeedInput = (value, route) => (dispatch) => {
	if (route === TEXT_TO_IMAGE) {
		dispatch(setStudioSeedInput(value));
	} else if (route === STUDIO) {
		dispatch(setCanvasSeedInput(value));
	}
};
export const randomiseSeedInputSelector = createSelector(
	[studioRandomiseSeedInputSelector, canvasRandomiseSeedInputSelector, (state, route) => route],
	(studioRandomiseSeedInput, canvasRandomiseSeedInput, route) => {
		if (route === TEXT_TO_IMAGE) return studioRandomiseSeedInput;
		if (route === STUDIO) return canvasRandomiseSeedInput;
		return [];
	},
);
export const setRandomiseSeedInput = (value, route) => (dispatch) => {
	if (route === TEXT_TO_IMAGE) {
		dispatch(setStudioRandomiseSeedInput(value));
	} else if (route === STUDIO) {
		dispatch(setCanvasRandomiseSeedInput(value));
	}
};

export const samplerInputSelector = createSelector(
	[studioSamplerInputSelector, canvasSamplerInputSelector, (state, route) => route],
	(studioSamplerInput, canvasSamplerInput, route) => {
		if (route === TEXT_TO_IMAGE) return studioSamplerInput;
		if (route === STUDIO) return canvasSamplerInput;
		return [];
	},
);
export const setSamplerInput = (value, route) => (dispatch) => {
	if (route === TEXT_TO_IMAGE) {
		dispatch(setStudioSamplerInput(value));
	} else if (route === STUDIO) {
		dispatch(setCanvasSamplerInput(value));
	}
};

export const samplingStepsInputSelector = createSelector(
	[studioSamplingStepsInputSelector, canvasSamplingStepsInputSelector, (state, route) => route],
	(studioSamplingStepsInput, canvasSamplingStepsInput, route) => {
		if (route === TEXT_TO_IMAGE) return studioSamplingStepsInput;
		if (route === STUDIO) return canvasSamplingStepsInput;
		return [];
	},
);
export const setSamplingStepsInput = (value, route) => (dispatch) => {
	if (route === TEXT_TO_IMAGE) {
		dispatch(setStudioSamplingStepsInput(value));
	} else if (route === STUDIO) {
		dispatch(setCanvasSamplingStepsInput(value));
	}
};

export const AIGuidanceInputSelector = createSelector(
	[studioAIGuidanceInputSelector, canvasAIGuidanceInputSelector, (state, route) => route],
	(studioAIGuidanceInput, canvasAIGuidanceInput, route) => {
		if (route === TEXT_TO_IMAGE) return studioAIGuidanceInput;
		if (route === STUDIO) return canvasAIGuidanceInput;
		return [];
	},
);
export const setAIGuidanceInput = (value, route) => (dispatch) => {
	if (route === TEXT_TO_IMAGE) {
		dispatch(setStudioAIGuidanceInput(value));
	} else if (route === STUDIO) {
		dispatch(setCanvasAIGuidanceInput(value));
	}
};

export const batchSizeInputSelector = createSelector(
	[studioBatchSizeInputSelector, canvasBatchSizeInputSelector, (state, route) => route],
	(studioBatchSizeInput, canvasBatchSizeInput, route) => {
		if (route === TEXT_TO_IMAGE) return studioBatchSizeInput;
		if (route === STUDIO) return canvasBatchSizeInput;
		return [];
	},
);
export const setBatchSizeInput = (value, route) => (dispatch) => {
	if (route === TEXT_TO_IMAGE) {
		dispatch(setStudioBatchSizeInput(value));
	} else if (route === STUDIO) {
		dispatch(setCanvasBatchSizeInput(value));
	}
};

export const aspectRatioInputSelector = createSelector(
	[studioAspectRatioInputSelector, canvasAspectRatioInputSelector, (state, route) => route],
	(studioAspectRatioInput, canvasAspectRatioInput, route) => {
		if (route === TEXT_TO_IMAGE) return studioAspectRatioInput;
		if (route === STUDIO) return canvasAspectRatioInput;
		return [];
	},
);
export const setAspectRatioInput = (value, route) => (dispatch) => {
	if (route === TEXT_TO_IMAGE) {
		dispatch(setStudioAspectRatioInput(value));
	} else if (route === STUDIO) {
		dispatch(setCanvasAspectRatioInput(value));
	}
};

// CONTROL NET
export const controlNetEnabledSelector = createSelector(
	[studioControlNetEnabledSelector, canvasControlNetEnabledSelector, (state, route) => route],
	(studioControlNetEnabled, canvasControlNetEnabled, route) => {
		if (route === TEXT_TO_IMAGE) return studioControlNetEnabled;
		if (route === STUDIO) return canvasControlNetEnabled;
		return [];
	},
);
export const setControlNetEnabled = (value, route) => (dispatch) => {
	if (route === TEXT_TO_IMAGE) {
		dispatch(setStudioControlNetEnabled(value));
	} else if (route === STUDIO) {
		dispatch(setCanvasControlNetEnabled(value));
	}
};

export const controlNetNonSDXLTypeSelector = createSelector(
	[
		studioControlNetNonSDXLTypeSelector,
		canvasControlNetNonSDXLTypeSelector,
		(state, route) => route,
	],
	(studioControlNetNonSDXLType, canvasControlNetNonSDXLType, route) => {
		if (route === TEXT_TO_IMAGE) return studioControlNetNonSDXLType;
		if (route === STUDIO) return canvasControlNetNonSDXLType;
		return [];
	},
);
export const setControlNetNonSDXLType = (value, route) => (dispatch) => {
	if (route === TEXT_TO_IMAGE) {
		dispatch(setStudioControlNetNonSDXLType(value));
	} else if (route === STUDIO) {
		dispatch(setCanvasControlNetNonSDXLType(value));
	}
};

export const controlNetSDXLTypeSelector = createSelector(
	[studioControlNetSDXLTypeSelector, canvasControlNetSDXLTypeSelector, (state, route) => route],
	(studioControlNetSDXLType, canvasControlNetSDXLType, route) => {
		if (route === TEXT_TO_IMAGE) return studioControlNetSDXLType;
		if (route === STUDIO) return canvasControlNetSDXLType;
		return [];
	},
);
export const setControlNetSDXLType = (value, route) => (dispatch) => {
	if (route === TEXT_TO_IMAGE) {
		dispatch(setStudioControlNetSDXLType(value));
	} else if (route === STUDIO) {
		dispatch(setCanvasControlNetSDXLType(value));
	}
};

export const controlNetFluxTypeSelector = createSelector(
	[studioControlNetFluxTypeSelector, canvasControlNetFluxTypeSelector, (state, route) => route],
	(studioControlNetFluxType, canvasControlNetFluxType, route) => {
		if (route === TEXT_TO_IMAGE) return studioControlNetFluxType;
		if (route === STUDIO) return canvasControlNetFluxType;
		return [];
	},
);
export const setControlNetFluxType = (value, route) => (dispatch) => {
	if (route === TEXT_TO_IMAGE) {
		dispatch(setStudioControlNetFluxType(value));
	} else if (route === STUDIO) {
		dispatch(setCanvasControlNetFluxType(value));
	}
};
export const controlNetImageSelector = createSelector(
	[studioControlNetImageSelector, canvasControlNetImageSelector, (state, route) => route],
	(studioControlNetImage, canvasControlNetImage, route) => {
		if (route === TEXT_TO_IMAGE) return studioControlNetImage;
		if (route === STUDIO) return canvasControlNetImage;
		return [];
	},
);
export const resetControlNetImage = (route) => (dispatch) => {
	if (route === TEXT_TO_IMAGE) {
		dispatch(resetStudioControlNetImage());
	} else if (route === STUDIO) {
		dispatch(resetCanvasControlNetImage());
	}
};
export const uploadControlNetImageFileAsync = (file, route) => (dispatch) => {
	if (route === TEXT_TO_IMAGE) {
		dispatch(uploadStudioControlNetImageFileAsync(file));
	} else if (route === STUDIO) {
		dispatch(uploadCanvasControlNetImageFileAsync(file));
	}
};
export const uploadControlNetImageLinkAsync =
	(link, route, isTemplate = false) =>
	(dispatch) => {
		dispatch(setIsTemplateAssetSelected(isTemplate));
		if (route === TEXT_TO_IMAGE) {
			dispatch(uploadStudioControlNetImageLinkAsync(link));
		} else if (route === STUDIO) {
			dispatch(uploadCanvasControlNetImageLinkAsync(link));
		}
	};

export const controlNetControlModeSelector = createSelector(
	[
		studioControlNetControlModeSelector,
		canvasControlNetControlModeSelector,
		(state, route) => route,
	],
	(studioControlNetControlMode, canvasControlNetControlMode, route) => {
		if (route === TEXT_TO_IMAGE) return studioControlNetControlMode;
		if (route === STUDIO) return canvasControlNetControlMode;
		return [];
	},
);
export const setControlNetControlMode = (value, route) => (dispatch) => {
	if (route === TEXT_TO_IMAGE) {
		dispatch(setStudioControlNetControlMode(value));
	} else if (route === STUDIO) {
		dispatch(setCanvasControlNetControlMode(value));
	}
};

export const controlNetResizeModeSelector = createSelector(
	[studioControlNetResizeModeSelector, canvasControlNetResizeModeSelector, (state, route) => route],
	(studioControlNetResizeMode, canvasControlNetResizeMode, route) => {
		if (route === TEXT_TO_IMAGE) return studioControlNetResizeMode;
		if (route === STUDIO) return canvasControlNetResizeMode;
		return [];
	},
);
export const setControlNetResizeMode = (value, route) => (dispatch) => {
	if (route === TEXT_TO_IMAGE) {
		dispatch(setStudioControlNetResizeMode(value));
	} else if (route === STUDIO) {
		dispatch(setCanvasControlNetResizeMode(value));
	}
};

// FACE_LOCK
export const facelockEnabledSelector = createSelector(
	[studioFacelockEnabledSelector, canvasFacelockEnabledSelector, (state, route) => route],
	(studioFacelockEnabled, canvasFacelockEnabled, route) => {
		if (route === TEXT_TO_IMAGE) return studioFacelockEnabled;
		if (route === STUDIO) return canvasFacelockEnabled;
		return [];
	},
);
export const setFacelockEnabled = (value, route) => (dispatch) => {
	if (route === TEXT_TO_IMAGE) {
		dispatch(setStudioFacelockEnabled(value));
	} else if (route === STUDIO) {
		dispatch(setCanvasFacelockEnabled(value));
	}
};

export const facelockImageSelector = createSelector(
	[studioFacelockImageSelector, canvasFacelockImageSelector, (state, route) => route],
	(studioFacelockImage, canvasFacelockImage, route) => {
		if (route === TEXT_TO_IMAGE) return studioFacelockImage;
		if (route === STUDIO) return canvasFacelockImage;
		return [];
	},
);
export const changeAnythingImageSelector = createSelector(
	[studioChangeAnythingImageSelector, (state, route) => route],
	(studioChangeAnythingImage, route) => {
		if (route === TEXT_TO_IMAGE) return studioChangeAnythingImage;
		return '';
	},
);
export const resetFacelockImage = (route) => (dispatch) => {
	if (route === TEXT_TO_IMAGE) {
		dispatch(resetStudioFacelockImage());
	} else if (route === STUDIO) {
		dispatch(resetCanvasFacelockImage());
	}
};
export const resetChangeAnythingImage = (route) => (dispatch) => {
	if (route === TEXT_TO_IMAGE) {
		dispatch(resetStudioChangeAnythingImage());
	}
};
export const uploadFacelockImageFileAsync = (file, route) => (dispatch) => {
	if (route === TEXT_TO_IMAGE) {
		dispatch(uploadStudioFaceLockImageFileAsync(file));
	} else if (route === STUDIO) {
		dispatch(uploadCanvasFaceLockImageFileAsync(file));
	}
};
export const uploadFacelockImageLinkAsync = (link, route) => (dispatch) => {
	if (route === TEXT_TO_IMAGE) {
		dispatch(uploadStudioFaceLockImageLinkAsync(link));
	} else if (route === STUDIO) {
		dispatch(uploadCanvasFaceLockImageLinkAsync(link));
	}
};

export const uploadChangeAnythingImageFileAsync = (file, route) => (dispatch) => {
	if (route === TEXT_TO_IMAGE) {
		dispatch(uploadStudioChangeAnythingImageFileAsync(file));
	}
};

export const uploadChangeAnythingImageLinkAsync = (link, route) => (dispatch) => {
	if (route === TEXT_TO_IMAGE) {
		dispatch(uploadStudioChangeAnythingImageLinkAsync(link));
	}
};

export const generateImageAPIStageSelector = createSelector(
	[
		generateStudioMediaAPIStageSelector,
		generateCanvasImageAPIStageSelector,
		(state, route) => route,
	],
	(generateStudioImageAPIStage, generateCanvasImageAPIStage, route) => {
		if (route === TEXT_TO_IMAGE) return generateStudioImageAPIStage;
		if (route === STUDIO) return generateCanvasImageAPIStage;
		return [];
	},
);

export const trueTouchImageSelector = createSelector(
	[studioTrueTouchImageSelector, canvasTrueTouchImageSelector, (state, route) => route],
	(studioTrueTouchImage, canvasTrueTouchImage, route) => {
		if (route === TEXT_TO_IMAGE) return studioTrueTouchImage;
		if (route === STUDIO) return canvasTrueTouchImage;
		return [];
	},
);
export const resetTrueTouchImage = (route) => (dispatch) => {
	if (route === TEXT_TO_IMAGE) {
		dispatch(resetStudioTrueTouchImage());
	}
	if (route === STUDIO) {
		dispatch(resetCanvasTrueTouchImage());
	}
};

export const uploadTrueTouchImageFileAsync = (file, route) => (dispatch) => {
	if (route === TEXT_TO_IMAGE) {
		dispatch(uploadStudioTrueTouchImageFileAsync(file));
	}
	if (route === STUDIO) {
		dispatch(uploadCanvasTrueTouchImageFileAsync(file));
	}
};

export const uploadTrueTouchImageLinkAsync = (file, route) => (dispatch) => {
	if (route === TEXT_TO_IMAGE) {
		dispatch(uploadStudioTrueTouchImageLinkAsync(file));
	}
	if (route === STUDIO) {
		dispatch(uploadCanvasTrueTouchImageLinkAsync(file));
	}
};

export const upscale8kImageSelector = createSelector(
	[studioUpscaleImageSelector, canvasUpscaleImageSelector, (state, route) => route],
	(studioUpscaleImage, canvasUpscaleImage, route) => {
		if (route === TEXT_TO_IMAGE) return studioUpscaleImage;
		if (route === STUDIO) return canvasUpscaleImage;
		return [];
	},
);

export const uploadUpscale8kImageLinkAsync = (link, route) => (dispatch) => {
	if (route === TEXT_TO_IMAGE) {
		dispatch(setStudioUpscaleImage(link));
	}
	if (route === STUDIO) {
		dispatch(setCanvasUpscaleImage(link));
	}
};

export const resetUpscale8kImageAsync = (route) => (dispatch) => {
	if (route === TEXT_TO_IMAGE) {
		dispatch(resetStudioUpscaleImage());
	}
	if (route === STUDIO) {
		dispatch(resetCanvasUpscaleImage());
	}
};

export const upscale8kOutputResolutionSelector = createSelector(
	[
		studioUpscaleOutputResolutionSelector,
		canvasUpscaleOutputResolutionSelector,
		(state, route) => route,
	],
	(studioUpscaleOutputResolution, canvasUpscaleOutputResolution, route) => {
		if (route === TEXT_TO_IMAGE) return studioUpscaleOutputResolution;
		if (route === STUDIO) return canvasUpscaleOutputResolution;
		return [];
	},
);

export const setUpscale8kOutputResolution = (outputResolution, route) => (dispatch) => {
	if (route === TEXT_TO_IMAGE) {
		dispatch(setStudioUpscaleOutputResolution(outputResolution));
	}
	if (route === STUDIO) {
		dispatch(setCanvasUpscaleOutputResolution(outputResolution));
	}
};

export const uploadLipSyncVideoAsync = (asset, route) => (dispatch) => {
	if (route === TEXT_TO_IMAGE) {
		dispatch(setStudioLipSyncVideoAssetData(asset));
	}
	if (route === STUDIO) {
		dispatch(setCanvasLipSyncVideoAssetData(asset));
	}
};

export const uploadLipSyncImageAsync = (asset, route) => (dispatch) => {
	if (route === TEXT_TO_IMAGE) {
		dispatch(setStudioLipSyncImageAssetData(asset));
	}
	if (route === STUDIO) {
		dispatch(setCanvasLipSyncImageAssetData(asset));
	}
};

export const uploadLipSyncAudioAssetAsync = (asset, route) => (dispatch) => {
	if (route === TEXT_TO_IMAGE) {
		dispatch(setStudioLipSyncAudioAssetData(asset));
	}
	if (route === STUDIO) {
		dispatch(setCanvasLipSyncAudioAssetData(asset));
	}
};

export const uploadLipSyncScriptAsync = (script, route) => (dispatch) => {
	if (route === TEXT_TO_IMAGE) {
		dispatch(setStudioLipSyncScript(script));
	}
	if (route === STUDIO) {
		dispatch(setCanvasLipSyncScript(script));
	}
};

export const uploadLipSyncVoiceAsync = (voice, route) => (dispatch) => {
	if (route === TEXT_TO_IMAGE) {
		dispatch(setStudioLipSyncVoiceData(voice));
	}
	if (route === STUDIO) {
		dispatch(setCanvasLipSyncVoiceData(voice));
	}
};

export const resetLipSyncAudioAssetAsync = (route) => (dispatch) => {
	if (route === TEXT_TO_IMAGE) {
		dispatch(resetStudioLipSyncAudioAssetData());
	}
	if (route === STUDIO) {
		dispatch(resetCanvasLipSyncAudioAssetData());
	}
};

export const resetLipSyncVideoAssetAsync = (route) => (dispatch) => {
	if (route === TEXT_TO_IMAGE) {
		dispatch(resetStudioLipSyncVideoAssetData());
	}
	if (route === STUDIO) {
		dispatch(resetCanvasLipSyncVideoAssetData());
	}
};

export const resetLipSyncImageAssetAsync = (route) => (dispatch) => {
	if (route === TEXT_TO_IMAGE) {
		dispatch(resetStudioLipSyncImageAssetData());
	}
	if (route === STUDIO) {
		dispatch(resetCanvasLipSyncImageAssetData());
	}
};

export const setIsLipSyncFreeVideoUsed = (isUsed, route) => (dispatch) => {
	if (route === TEXT_TO_IMAGE) {
		dispatch(setStudioIsLipSyncFreeVideoUsed(isUsed));
	}
	if (route === STUDIO) {
		dispatch(setCanvasIsLipSyncFreeVideoUsed(isUsed));
	}
};

export const lipSyncVideoSelector = createSelector(
	[
		studioLipSyncVideoAssetDataSelector,
		canvasLipSyncVideoAssetDataSelector,
		(state, route) => route,
	],
	(studioLipSyncVideo, canvasLipSyncVideo, route) => {
		if (route === TEXT_TO_IMAGE) return studioLipSyncVideo;
		if (route === STUDIO) return canvasLipSyncVideo;
		return [];
	},
);

export const lipSyncImageSelector = createSelector(
	[
		studioLipSyncImageAssetDataSelector,
		canvasLipSyncImageAssetDataSelector,
		(state, route) => route,
	],
	(studioLipSyncImage, canvasLipSyncImage, route) => {
		if (route === TEXT_TO_IMAGE) return studioLipSyncImage;
		if (route === STUDIO) return canvasLipSyncImage;
		return [];
	},
);

export const lipSyncAudioAssetSelector = createSelector(
	[
		studioLipSyncAudioAssetDataSelector,
		canvasLipSyncAudioAssetDataSelector,
		(state, route) => route,
	],
	(studioLipSyncAudio, canvasLipSyncAudio, route) => {
		if (route === TEXT_TO_IMAGE) return studioLipSyncAudio;
		if (route === STUDIO) return canvasLipSyncAudio;
		return [];
	},
);

export const lipSyncVoiceSelector = createSelector(
	[studioLipSyncVoiceDataSelector, canvasLipSyncVoiceDataSelector, (state, route) => route],
	(studioLipSyncVoiceData, canvasLipSyncVoiceData, route) => {
		if (route === TEXT_TO_IMAGE) return studioLipSyncVoiceData;
		if (route === STUDIO) return canvasLipSyncVoiceData;
		return [];
	},
);

export const lipSyncScriptSelector = createSelector(
	[studioLipSyncScriptSelector, canvasLipSyncScriptSelector, (state, route) => route],
	(studioLipSyncScript, canvasLipSyncScript, route) => {
		if (route === TEXT_TO_IMAGE) return studioLipSyncScript;
		if (route === STUDIO) return canvasLipSyncScript;
		return [];
	},
);

export const lipSyncIsFreeVideoUsedSelector = createSelector(
	[
		studioLipSyncIsFreeVideoUsedSelector,
		canvasLipSyncIsFreeVideoUsedSelector,
		(state, route) => route,
	],
	(studioLipSyncIsFreeVideoUsed, canvasLipSyncIsFreeVideoUsed, route) => {
		if (route === TEXT_TO_IMAGE) return studioLipSyncIsFreeVideoUsed;
		if (route === STUDIO) return canvasLipSyncIsFreeVideoUsed;
		return false;
	},
);

export const trueTouchScaleFactorSelector = createSelector(
	[canvasTrueTouchScaleFactorSelector, studioTrueTouchScaleFactorSelector, (state, route) => route],
	(canvasTrueTouchScaleFactor, studioTrueTouchScaleFactor, route) => {
		if (route === TEXT_TO_IMAGE) return studioTrueTouchScaleFactor;
		if (route === STUDIO) return canvasTrueTouchScaleFactor;
		return [];
	},
);
export const setTrueTouchScaleFactor = (value, route) => (dispatch) => {
	if (route === TEXT_TO_IMAGE) {
		dispatch(setStudioTrueTouchScaleFactor(value));
	} else if (route === STUDIO) {
		dispatch(setCanvasTrueTouchScaleFactor(value));
	}
};
export const trueTouchRealismEffectSelector = createSelector(
	[
		canvasTrueTouchRealismEffectSelector,
		studioTrueTouchRealismEffectSelector,
		(state, route) => route,
	],
	(canvasTrueTouchRealismEffect, trueTouchTrueTouchRealismEffect, route) => {
		if (route === TEXT_TO_IMAGE) return trueTouchTrueTouchRealismEffect;
		if (route === STUDIO) return canvasTrueTouchRealismEffect;
		return [];
	},
);
export const setTrueTouchRealismEffect = (value, route) => (dispatch) => {
	if (route === TEXT_TO_IMAGE) {
		dispatch(setStudioTrueTouchRealismEffect(value));
	} else if (route === STUDIO) {
		dispatch(setCanvasTrueTouchRealismEffect(value));
	}
};
export const isTrueTouchImageUploadingSelector = createSelector(
	[
		canvasIsTrueTouchImageUploadingSelector,
		studioIsTrueTouchImageUploadingSelector,
		(state, route) => route,
	],
	(canvasIsTrueTouchImageUploading, studioIsTrueTouchImageUploading, route) => {
		if (route === TEXT_TO_IMAGE) return studioIsTrueTouchImageUploading;
		if (route === STUDIO) return canvasIsTrueTouchImageUploading;
		return [];
	},
);

export const handleSetSelectedLora = (lora, route) => (dispatch) => {
	if (route === TEXT_TO_IMAGE) {
		dispatch(handleSetStudioSelectedLora(lora));
	} else if (route === STUDIO) {
		dispatch(handleAddCanvasSelectedLora(lora));
	}
};

export const randomisePromptAPIStageSelector = createSelector(
	[
		randomiseStudioPromptAPIStageSelector,
		randomiseCanvasPromptAPIStageSelector,
		(state, route) => route,
	],
	(randomiseStudioPromptAPIStage, randomiseCanvasPromptAPIStage, route) => {
		if (route === TEXT_TO_IMAGE) return randomiseStudioPromptAPIStage;
		if (route === STUDIO) return randomiseCanvasPromptAPIStage;
		return [];
	},
);
export const handleRandomisePrompt = (route) => (dispatch) => {
	if (route === TEXT_TO_IMAGE) {
		dispatch(randomiseStudioPromptAsync());
	} else if (route === STUDIO) {
		dispatch(randomiseCanvasPromptAsync());
	}
};

export const handleSelectedCharacter = createSelector(
	[
		selectedCharacterSelector,
		studioSelectedCharacterSelector,
		homeSelectedCharacterSelector,
		(state, route) => route,
	],
	(characterSelected, studioCharacterSelected, homeCharacterSelected, route) => {
		if (route === TEXT_TO_IMAGE) return studioCharacterSelected;
		if (route === STUDIO) return characterSelected;
		if (route === APP) return homeCharacterSelected;
		return null;
	},
);

export const handleControlWeightSelector = createSelector(
	[
		controlWeightSelector,
		controlWeightSelectorStudio,
		controlWeightHomeSelector,
		(state, route) => route,
	],
	(controlWeight, controlWeightStudio, controlWeightHome, route) => {
		if (route === TEXT_TO_IMAGE) return controlWeightStudio;
		if (route === STUDIO) return controlWeight;
		if (route === APP) return controlWeightHome;
		return null;
	},
);
export const handleControlWeightChange = (value, route) => (dispatch) => {
	if (route === TEXT_TO_IMAGE) {
		dispatch(setControlWeightStudio(value));
	} else if (route === STUDIO) {
		dispatch(setControlWeight(value));
	}
};

export const handleRealisticFaceSwapSelector = createSelector(
	[
		realisticFaceSwapSelector,
		realisticFaceSwapSelectorStudio,
		realisticFaceSwapHomeSelector,
		(state, route) => route,
	],
	(enableRealisticFaceSwap, enableRealisticFaceSwapStudio, enableRealisticFaceSwapHome, route) => {
		if (route === TEXT_TO_IMAGE) return enableRealisticFaceSwapStudio;
		if (route === STUDIO) return enableRealisticFaceSwap;
		if (route === APP) return enableRealisticFaceSwapHome;
		return null;
	},
);

export const handleRealisticFaceSwapToggle = (value, route) => (dispatch) => {
	if (route === TEXT_TO_IMAGE) {
		dispatch(toggleEnableRealisticFaceSwapStudio(value));
	} else if (route === STUDIO) {
		dispatch(toggleEnableRealisticFaceSwap(value));
	}
};

export const controlNetModelsSelector = createSelector(
	[canvasControlNetModelsSelector, studioControlNetModelsSelector, (state, route) => route],
	(controlNetModelsCanvas, controlNetModelsStudio, route) => {
		if (route === TEXT_TO_IMAGE) return controlNetModelsStudio;
		if (route === STUDIO) return controlNetModelsCanvas;
		return null;
	},
);
export const controlNetModelsApiStageSelector = createSelector(
	[
		canvasControlNetModelsApiStageSelector,
		studioControlNetModelsApiStageSelector,
		(state, route) => route,
	],
	(controlNetModelsCanvasApiStage, controlNetModelsStudioApiStage, route) => {
		if (route === TEXT_TO_IMAGE) return controlNetModelsStudioApiStage;
		if (route === STUDIO) return controlNetModelsCanvasApiStage;
		return null;
	},
);

export const lightningModelsSelector = createSelector(
	[canvasLightningModelsSelector, studioLightningModelsSelector, (state, route) => route],
	(lightningModelsCanvas, lightningModelsStudio, route) => {
		if (route === TEXT_TO_IMAGE) return lightningModelsStudio;
		if (route === STUDIO) return lightningModelsCanvas;
		return null;
	},
);

export const selectedLightningModelsSelector = createSelector(
	[
		canvasSelectedLightningModelsSelector,
		studioSelectedLightningModelsSelector,
		(state, route) => route,
	],
	(selectedLightningModelsCanvas, selectedLightningModelsStudio, route) => {
		if (route === TEXT_TO_IMAGE) return selectedLightningModelsStudio;
		if (route === STUDIO) return selectedLightningModelsCanvas;
		return null;
	},
);

export const handleSelectedLightningModels = (lightningModel, route) => (dispatch) => {
	if (route === TEXT_TO_IMAGE) {
		dispatch(setStudioSelectedLightningModels(lightningModel));
	} else if (route === STUDIO) {
		dispatch(handleSetCanvasSelectedLightningModel([lightningModel]));
	}
};

export const fluxModelsSelector = createSelector(
	[canvasFluxModelsSelector, studioFluxModelsSelector, (state, route) => route],
	(fluxModelsCanvas, fluxModelsStudio, route) => {
		if (route === TEXT_TO_IMAGE) return fluxModelsStudio;
		if (route === STUDIO) return fluxModelsCanvas;
		return null;
	},
);

export const selectedFluxModelsSelector = createSelector(
	[canvasSelectedFluxModelsSelector, studioSelectedFluxModelsSelector, (state, route) => route],
	(selectedFluxModelsCanvas, selectedFluxModelsStudio, route) => {
		if (route === TEXT_TO_IMAGE) return selectedFluxModelsStudio;
		if (route === STUDIO) return selectedFluxModelsCanvas;
		return null;
	},
);

export const handleSelectedFluxModels = (fluxModel, route) => (dispatch) => {
	if (route === TEXT_TO_IMAGE) {
		dispatch(setStudioSelectedFluxModels(fluxModel));
	} else if (route === STUDIO) {
		dispatch(handleSetCanvasSelectedFluxModel(fluxModel));
	}
};

export const isFluxTabSelectedSelector = createSelector(
	[currentGenerationStyleTabSelector, studioGenerationSidebarViewSelector, (state, route) => route],
	(canvasGenerationStyleTab, studioGenerationStyleTab, route) => {
		if (route === TEXT_TO_IMAGE) return studioGenerationStyleTab === StudioSidebarViews.FLUX;
		if (route === STUDIO) return canvasGenerationStyleTab === GenerationStyleTabs.FLUX_MODE;
		return null;
	},
);
export const feedAPIStageSelector = createSelector(
	[getStudioFeedAPIStageSelector, canvasFeedAPIStageSelector, (state, route) => route],
	(feedStudioAPIStage, feedCanvasAPIStage, route) => {
		if (route === TEXT_TO_IMAGE) return feedStudioAPIStage;
		if (route === STUDIO) return feedCanvasAPIStage;
		return null;
	},
);

export const isFluxModeSelected = createSelector(
	[currentGenerationStyleTabSelector, studioGenerationSidebarViewSelector, (state, route) => route],
	(selectedGenerationTabCanvas, selectedGenerationTabStudio, route) => {
		if (route === TEXT_TO_IMAGE) {
			return selectedGenerationTabStudio === StudioSidebarViews.FLUX;
		}
		if (route === STUDIO) return selectedGenerationTabCanvas === GenerationStyleTabs.FLUX_MODE;
		return null;
	},
);
export const uploadVideoAnyoneImageLinkAsync = (link, assetId, route) => (dispatch) => {
	if (route === TEXT_TO_IMAGE) {
		dispatch(setStudioVideoAnyoneImage(link));
		dispatch(setStudioVideoAnyoneImageId(assetId));
	}
	if (route === STUDIO) {
		dispatch(setCanvasVideoAnyoneImage(link));
		dispatch(setCanvasVideoAnyoneImageId(assetId));
	}
};

export const handleVideoAnyoneDuration = (duration, route) => (dispatch) => {
	if (route === TEXT_TO_IMAGE) {
		dispatch(setStudioVideoAnyoneDuration(duration));
	} else if (route === STUDIO) {
		dispatch(setCanvasVideoAnyoneDuration(duration));
	}
};
export const videoAnyoneDurationSelector = createSelector(
	[canvasVideoAnyoneDurationSelector, studioVideoAnyoneDurationSelector, (state, route) => route],
	(canvasVideoAnyoneDuration, studioVideoAnyoneDuration, route) => {
		if (route === TEXT_TO_IMAGE) return studioVideoAnyoneDuration;
		if (route === STUDIO) return canvasVideoAnyoneDuration;
		return null;
	},
);

export const resetVideoAnyoneImageAsync = (route) => (dispatch) => {
	if (route === TEXT_TO_IMAGE) {
		dispatch(resetStudioVideoAnyoneImage());
	}
	if (route === STUDIO) {
		dispatch(resetCanvasVideoAnyoneImage());
	}
};
export const videoAnyoneImageSelector = createSelector(
	[canvasVideoAnyoneImageSelector, studioVideoAnyoneImageSelector, (state, route) => route],
	(canvasVideoAnyoneImage, studioVideoAnyoneImage, route) => {
		if (route === TEXT_TO_IMAGE) return studioVideoAnyoneImage;
		if (route === STUDIO) return canvasVideoAnyoneImage;
		return null;
	},
);
export const videoFaceSwapImageSelector = createSelector(
	[canvasVideoFaceSwapImageSelector, studioVideoFaceSwapImageSelector, (state, route) => route],
	(canvasVideoFaceSwapImage, studioVideoFaceSwapImage, route) => {
		if (route === TEXT_TO_IMAGE) return studioVideoFaceSwapImage;
		if (route === STUDIO) return canvasVideoFaceSwapImage;
		return null;
	},
);

export const videoFaceSwapVideoSelector = createSelector(
	[canvasVideoFaceSwapVideoSelector, studioVideoFaceSwapVideoSelector, (state, route) => route],
	(canvasVideoFaceSwapVideo, studioVideoFaceSwapVideo, route) => {
		if (route === TEXT_TO_IMAGE) return studioVideoFaceSwapVideo;
		if (route === STUDIO) return canvasVideoFaceSwapVideo;
		return null;
	},
);

export const videoFaceSwapRenderingPreferenceSelector = createSelector(
	[
		canvasVideoFaceSwapRenderingPrefSelector,
		studioVideoFaceSwapRendingPrefSelector,
		(state, route) => route,
	],
	(canvasVideoFaceSwapRenderingPref, studioVideoFaceSwapRenderingPref, route) => {
		if (route === TEXT_TO_IMAGE) return studioVideoFaceSwapRenderingPref;
		if (route === STUDIO) return canvasVideoFaceSwapRenderingPref;
		return null;
	},
);

export const videoAnyonePromptSelector = createSelector(
	[canvasVideoAnyonePromptSelector, studioVideoAnyonePromptSelector, (state, route) => route],
	(canvasVideoAnyonePrompt, studioVideoAnyonePrompt, route) => {
		if (route === TEXT_TO_IMAGE) return studioVideoAnyonePrompt;
		if (route === STUDIO) return canvasVideoAnyonePrompt;
		return null;
	},
);
export const handleVideoAnyonePrompt = (prompt, route) => (dispatch) => {
	if (route === TEXT_TO_IMAGE) {
		dispatch(setStudioVideoAnyonePrompt(prompt));
	} else if (route === STUDIO) {
		dispatch(setCanvasVideoAnyonePrompt(prompt));
	}
};

export const faceReplaceImageSelector = createSelector(
	[canvasFaceReplaceImageSelector, studioFaceReplaceImageSelector, (state, route) => route],
	(canvasFaceReplaceImage, studioFaceReplaceImage, route) => {
		if (route === TEXT_TO_IMAGE) return studioFaceReplaceImage;
		if (route === STUDIO) return canvasFaceReplaceImage;
		return null;
	},
);

export const uploadFaceReplaceImageLinkAsync = (link, id, route) => (dispatch) => {
	if (route === TEXT_TO_IMAGE) {
		dispatch(setStudioFaceReplaceImage(link));
		dispatch(setStudioFaceReplaceImageId(id));
	}
	if (route === STUDIO) {
		dispatch(setCanvasFaceReplaceImage(link));
		dispatch(setCanvasFaceReplaceImageId(id));
	}
};

export const resetFaceReplaceImageAsync = (route) => (dispatch) => {
	if (route === TEXT_TO_IMAGE) {
		dispatch(resetStudioFaceReplaceImage());
	}
	if (route === STUDIO) {
		dispatch(resetCanvasFaceReplaceImage());
	}
};

export const setFaceReplaceTargetImageIndex = (index, route) => (dispatch) => {
	if (route === TEXT_TO_IMAGE) {
		dispatch(setStudioFaceReplaceTargetImageIndex(index));
	} else if (route === STUDIO) {
		dispatch(setCanvasFaceReplaceTargetImageIndex(index));
	}
};

export const faceReplaceTargetImageIndexSelector = createSelector(
	[
		studioFaceReplaceTargetImageIndexSelector,
		canvasFaceReplaceTargetImageIndexSelector,
		(state, route) => route,
	],
	(studioFaceReplaceTargetImageIndex, canvasFaceReplaceTargetImageIndex, route) => {
		if (route === TEXT_TO_IMAGE) return studioFaceReplaceTargetImageIndex;
		if (route === STUDIO) return canvasFaceReplaceTargetImageIndex;
		return -1;
	},
);

export const uploadFaceReplaceTargetImage = (image, index, id, route) => (dispatch) => {
	if (route === TEXT_TO_IMAGE) {
		dispatch(setStudioFaceReplaceTargetImages({ image, index, id }));
	} else if (route === STUDIO) {
		dispatch(setCanvasFaceReplaceTargetImages({ image, index, id }));
	}
};

export const removeFaceReplaceTargetImage = (index, route) => (dispatch) => {
	if (route === TEXT_TO_IMAGE) {
		dispatch(removeStudioFaceReplaceTargetImage(index));
	} else if (route === STUDIO) {
		dispatch(removeCanvasFaceReplaceTargetImage(index));
	}
};

export const faceReplaceTargetImagesSelector = createSelector(
	[
		studioFaceReplaceTargetImagesSelector,
		canvasFaceReplaceTargetImagesSelector,
		(state, route) => route,
	],
	(studioFaceReplaceTargetImages, canvasFaceReplaceTargetImages, route) => {
		if (route === TEXT_TO_IMAGE) return studioFaceReplaceTargetImages;
		if (route === STUDIO) return canvasFaceReplaceTargetImages;
		return [];
	},
);

export const resetFaceReplaceTargetImages = (route) => (dispatch) => {
	if (route === TEXT_TO_IMAGE) {
		dispatch(resetStudioFaceReplaceTargetImages());
	} else if (route === STUDIO) {
		dispatch(resetCanvasFaceReplaceTargetImages());
	}
};

export const getFaceReplaceCost = (route) => (dispatch) => {
	if (route === TEXT_TO_IMAGE) {
		dispatch(getStudioFaceReplaceCostAsync());
	} else if (route === STUDIO) {
		dispatch(getCanvasFaceReplaceCostAsync());
	}
};

export const faceReplaceCostAPIStageSelector = createSelector(
	[
		canvasFaceReplaceCostAPIStageSelector,
		studioFaceReplaceCostAPIStageSelector,
		(state, route) => route,
	],
	(canvasFaceReplaceCostAPIStage, studioFaceReplaceCostAPIStage, route) => {
		if (route === TEXT_TO_IMAGE) return studioFaceReplaceCostAPIStage;
		if (route === STUDIO) return canvasFaceReplaceCostAPIStage;
		return null;
	},
);

export const faceReplaceCostSelector = createSelector(
	[canvasFaceReplaceCostSelector, studioFaceReplaceCostSelector, (state, route) => route],
	(canvasFaceReplaceCost, studioFaceReplaceCost, route) => {
		if (route === TEXT_TO_IMAGE) return studioFaceReplaceCost;
		if (route === STUDIO) return canvasFaceReplaceCost;
		return null;
	},
);

export const toggleMediaFeedbackModal = (value, route) => (dispatch) => {
	if (route === TEXT_TO_IMAGE) {
		dispatch(toggleStudioMediaFeedbackModal(value));
	}
	if (route === STUDIO) {
		dispatch(toggleCanvasMediaFeedbackModal(value));
	}
};

export const isMediaFeedbackModalOpen = createSelector(
	[studioMediaFeedbackModalSelector, canvasMediaFeedbackModalSelector, (state, route) => route],
	(studioMediaFeedbackModal, canvasMediaFeedbackModal, route) => {
		if (route === TEXT_TO_IMAGE) return studioMediaFeedbackModal;
		if (route === STUDIO) return canvasMediaFeedbackModal;
		return false;
	},
);

export const currentGenerationViewSelector = createSelector(
	[currentGenerationStyleTabSelector, studioGenerationSidebarViewSelector, (state, route) => route],
	(canvasGenerationSidebarView, studioGenerationSidebarView, route) => {
		if (route === TEXT_TO_IMAGE) return studioGenerationSidebarView;
		if (route === STUDIO) return canvasGenerationSidebarView;
		return false;
	},
);

export const setCurrentGenerationView = (value, route) => (dispatch) => {
	if (route === TEXT_TO_IMAGE) {
		dispatch(setStudioGenerationSidebarView(value));
	}
	if (route === STUDIO) {
		dispatch(setCurrentGenerationStyleTab(value));
	}
};

export const MediaTypeSelector = createSelector(
	[canvasMediaTypeSelector, studioMediaTypeSelector, (state, route) => route],
	(canvasMediaType, studioMediaType, route) => {
		if (route === TEXT_TO_IMAGE) return studioMediaType;
		if (route === STUDIO) return canvasMediaType;
		return false;
	},
);

export const handleMediaTypeView = (value, route) => (dispatch) => {
	if (route === TEXT_TO_IMAGE) {
		dispatch(setStudioAppBarMediaType(value));
	}
	if (route === STUDIO) {
		dispatch(setCanvasAppBarMediaType(value));
	}
};

export const mediaToolsSidebarViewSelector = createSelector(
	[canvasMediaToolsSidebarViewSelector, studioSidebarViewSelector, (state, route) => route],
	(canvasMediaToolsSidebarView, studioMediaToolsSidebarView, route) => {
		if (route === TEXT_TO_IMAGE) return studioMediaToolsSidebarView;
		if (route === STUDIO) return canvasMediaToolsSidebarView;
		return false;
	},
);

export const handleMediaToolsSidebarView = (value, route) => (dispatch) => {
	if (route === TEXT_TO_IMAGE) {
		dispatch(setStudioSidebarView(value));
	}
	if (route === STUDIO) {
		dispatch(setCanvasMediaToolsSidebarView(value));
	}
};

export const setMusicVideoAudioAssetAsync = (asset, route) => (dispatch) => {
	if (route === TEXT_TO_IMAGE) {
		dispatch(setStudioMusicVideoAudioAssetData(asset));
	}
	if (route === STUDIO) {
		dispatch(setCanvasMusicVideoAudioAssetData(asset));
	}
};

export const musicVideoAudioAssetSelector = createSelector(
	[
		studioMusicVideoAudioAssetDataSelector,
		canvasMusicVideoAudioAssetDataSelector,
		(state, route) => route,
	],
	(studioMusicVideoAudio, canvasMusicVideoAudio, route) => {
		if (route === TEXT_TO_IMAGE) return studioMusicVideoAudio;
		if (route === STUDIO) return canvasMusicVideoAudio;
		return [];
	},
);

export const setMusicVideoTheme = (theme, route) => (dispatch) => {
	if (route === TEXT_TO_IMAGE) {
		dispatch(setStudioMusicVideoTheme(theme));
	}
	if (route === STUDIO) {
		dispatch(setCanvasMusicVideoTheme(theme));
	}
};

export const musicVideoThemeSelector = createSelector(
	[studioMusicVideoThemeSelector, canvasMusicVideoThemeSelector, (state, route) => route],
	(studioMusicVideoTheme, canvasMusicVideoTheme, route) => {
		if (route === TEXT_TO_IMAGE) return studioMusicVideoTheme;
		if (route === STUDIO) return canvasMusicVideoTheme;
		return [];
	},
);

export const resetMusicVideoAudioAssetAsync = (route) => (dispatch) => {
	if (route === TEXT_TO_IMAGE) {
		dispatch(resetStudioMusicVideoAudioAssetData());
	}
	if (route === STUDIO) {
		dispatch(resetCanvasMusicVideoAudioAssetData());
	}
};

export const setMusicVideoCharacter = (character, route) => (dispatch) => {
	if (route === TEXT_TO_IMAGE) {
		dispatch(setStudioMusicVideoCharacter(character));
	}
	if (route === STUDIO) {
		dispatch(setCanvasMusicVideoCharacter(character));
	}
};

export const musicVideoCharacterSelector = createSelector(
	[studioMusicVideoCharacterSelector, canvasMusicVideoCharacterSelector, (state, route) => route],
	(studioMusicVideoCharacter, canvasMusicVideoCharacter, route) => {
		if (route === TEXT_TO_IMAGE) return studioMusicVideoCharacter;
		if (route === STUDIO) return canvasMusicVideoCharacter;
		return [];
	},
);

export const setMusicVideoAspectRatio = (aspectRatio, route) => (dispatch) => {
	if (route === TEXT_TO_IMAGE) {
		dispatch(setStudioMusicVideoAspectRatio(aspectRatio));
	}
	if (route === STUDIO) {
		dispatch(setCanvasMusicVideoAspectRatio(aspectRatio));
	}
};

export const musicVideoAspectRatioSelector = createSelector(
	[
		studioMusicVideoAspectRatioSelector,
		canvasMusicVideoAspectRatioSelector,
		(state, route) => route,
	],
	(studioMusicVideoAspectRatio, canvasMusicVideoAspectRatio, route) => {
		if (route === TEXT_TO_IMAGE) return studioMusicVideoAspectRatio;
		if (route === STUDIO) return canvasMusicVideoAspectRatio;
		return AspectRatio.WIDESCREEN;
	},
);

export const musicVideoTemplatesDataSelector = createSelector(
	[
		studioMusicVideoTemplatesDataSelector,
		canvasMusicVideoTemplatesDataSelector,
		(state, route) => route,
	],
	(studioMusicVideoTemplates, canvasMusicVideoTemplates, route) => {
		if (route === TEXT_TO_IMAGE) return studioMusicVideoTemplates;
		if (route === STUDIO) return canvasMusicVideoTemplates;
		return [];
	},
);
export const musicVideoTemplatesApiStageSelector = createSelector(
	[
		studioMusicVideoTemplatesApiStageSelector,
		canvasMusicVideoTemplatesApiStageSelector,
		(state, route) => route,
	],
	(studioMusicVideoTemplatesApiStage, canvasMusicVideoTemplatesApiStage, route) => {
		if (route === TEXT_TO_IMAGE) return studioMusicVideoTemplatesApiStage;
		if (route === STUDIO) return canvasMusicVideoTemplatesApiStage;
		return [];
	},
);

// clothing change
export const clothingInputImageSelector = createSelector(
	[studioClothingInputImageSelector, canvasClothingInputImageSelector, (state, route) => route],
	(studioClothingInputImage, canvasClothingInputImage, route) => {
		if (route === TEXT_TO_IMAGE) return studioClothingInputImage;
		if (route === STUDIO) return canvasClothingInputImage;
		return [];
	},
);
export const uploadClothingInputImageLinkAsync = (link, id, route) => (dispatch) => {
	if (route === TEXT_TO_IMAGE) {
		dispatch(setStudioClothingInputImage({ id, url: link }));
	}
	if (route === STUDIO) {
		dispatch(setCanvasClothingInputImage({ id, url: link }));
	}
};
export const resetClothingInputImage = (route) => (dispatch) => {
	if (route === TEXT_TO_IMAGE) {
		dispatch(resetStudioClothingInputImage());
	}
	if (route === STUDIO) {
		dispatch(resetCanvasClothingInputImage());
	}
};

export const clothingTargetImageSelector = createSelector(
	[studioClothingTargetImageSelector, canvasClothingTargetImageSelector, (state, route) => route],
	(studioClothingTargetImage, canvasClothingTargetImage, route) => {
		if (route === TEXT_TO_IMAGE) return studioClothingTargetImage;
		if (route === STUDIO) return canvasClothingTargetImage;
		return [];
	},
);
export const uploadClothingTargetImageLinkAsync = (link, id, route) => (dispatch) => {
	if (route === TEXT_TO_IMAGE) {
		dispatch(setStudioClothingTargetImage({ id, url: link }));
	}
	if (route === STUDIO) {
		dispatch(setCanvasClothingTargetImage({ id, url: link }));
	}
};
export const resetClothingTargetImage = (route) => (dispatch) => {
	if (route === TEXT_TO_IMAGE) {
		dispatch(resetStudioClothingTargetImage());
	}
	if (route === STUDIO) {
		dispatch(resetCanvasClothingTargetImage());
	}
};

export const clothingTypeValueSelector = createSelector(
	[studioClothingTypeValueSelector, canvasClothingTypeValueSelector, (state, route) => route],
	(studioClothingTypeValue, canvasClothingTypeValue, route) => {
		if (route === TEXT_TO_IMAGE) return studioClothingTypeValue;
		if (route === STUDIO) return canvasClothingTypeValue;
		return [];
	},
);
export const handleClothingTypeValue = (value, route) => (dispatch) => {
	if (route === TEXT_TO_IMAGE) {
		dispatch(setStudioClothingTypeValue(value));
	}
	if (route === STUDIO) {
		dispatch(setCanvasClothingTypeValue(value));
	}
};

// Product Photo
export const productPhotoImageSelector = createSelector(
	[studioProductPhotoImageSelector, canvasProductPhotoImageSelector, (state, route) => route],
	(studioProductPhotoInputImage, canvasProductPhotoInputImage, route) => {
		if (route === TEXT_TO_IMAGE) return studioProductPhotoInputImage;
		if (route === STUDIO) return canvasProductPhotoInputImage;
		return null;
	},
);

export const setProductPhotoImageAsync = (link, id, route, displayOrder) => (dispatch) => {
	if (route === TEXT_TO_IMAGE) {
		dispatch(setStudioProductPhotoImage({ id, url: link, displayOrder }));
	}
	if (route === STUDIO) {
		dispatch(setCanvasProductPhotoImage({ id, url: link, displayOrder }));
	}
};

export const resetProductPhotoImage = (route) => (dispatch) => {
	if (route === TEXT_TO_IMAGE) {
		dispatch(resetStudioProductPhotoImage());
	}
	if (route === STUDIO) {
		dispatch(resetCanvasProductPhotoImage());
	}
};

export const productPhotoBackgroundImageSelector = createSelector(
	[
		studioProductPhotoBackgroundImageSelector,
		canvasProductPhotoBackgroundImageSelector,
		(state, route) => route,
	],
	(studioProductPhotoBackgroundImage, canvasProductPhotoBackgroundImage, route) => {
		if (route === TEXT_TO_IMAGE) return studioProductPhotoBackgroundImage;
		if (route === STUDIO) return canvasProductPhotoBackgroundImage;
		return null;
	},
);

export const setProductPhotoBackgroundImageAsync =
	(link, id, route, displayOrder) => (dispatch) => {
		if (route === TEXT_TO_IMAGE) {
			dispatch(setStudioProductPhotoBackgroundImage({ id, url: link, displayOrder }));
		}
		if (route === STUDIO) {
			dispatch(setCanvasProductPhotoBackgroundImage({ id, url: link, displayOrder }));
		}
	};

export const resetProductPhotoBackgroundImage = (route) => (dispatch) => {
	if (route === TEXT_TO_IMAGE) {
		dispatch(resetStudioProductPhotoBackgroundImage());
	}
	if (route === STUDIO) {
		dispatch(resetCanvasProductPhotoBackgroundImage());
	}
};

export const productPhotoModeSelector = createSelector(
	[studioProductPhotoModeSelector, canvasProductPhotoModeSelector, (state, route) => route],
	(studioProductPhotoMode, canvasProductPhotoMode, route) => {
		if (route === TEXT_TO_IMAGE) return studioProductPhotoMode;
		if (route === STUDIO) return canvasProductPhotoMode;
		return null;
	},
);

export const setProductPhotoMode = (mode, route) => (dispatch) => {
	if (route === TEXT_TO_IMAGE) {
		dispatch(setStudioProductPhotoMode(mode));
	}
	if (route === STUDIO) {
		dispatch(setCanvasProductPhotoMode(mode));
	}
};

export const productPhotoReflectionSelector = createSelector(
	[
		studioProductPhotoReflectionSelector,
		canvasProductPhotoReflectionSelector,
		(state, route) => route,
	],
	(studioProductPhotoReflection, canvasProductPhotoReflection, route) => {
		if (route === TEXT_TO_IMAGE) return studioProductPhotoReflection;
		if (route === STUDIO) return canvasProductPhotoReflection;
		return null;
	},
);

export const setProductPhotoReflection = (value, route) => (dispatch) => {
	if (route === TEXT_TO_IMAGE) {
		dispatch(setStudioProductPhotoReflection(value));
	}
	if (route === STUDIO) {
		dispatch(setCanvasProductPhotoReflection(value));
	}
};

export const productPhotoRemoveImageBackgroundAsync = (inputImageId, route) => (dispatch) => {
	if (route === TEXT_TO_IMAGE) {
		dispatch(productPhotoStudioRemoveImageBackgroundAsync(inputImageId));
	}
	if (route === STUDIO) {
		dispatch(productPhotoCanvasRemoveImageBackgroundAsync(inputImageId));
	}
};

export const productPhotoWithoutBackgroundImageApiStageSelector = createSelector(
	[
		studioProductPhotoWithoutBackgroundImageApiStageSelector,
		canvasProductPhotoWithoutBackgroundImageApiStageSelector,
		(state, route) => route,
	],
	(
		studioProductPhotoWithoutBackgroundImageApiStage,
		canvasProductPhotoWithoutBackgroundImageApiStage,
		route,
	) => {
		if (route === TEXT_TO_IMAGE) return studioProductPhotoWithoutBackgroundImageApiStage;
		if (route === STUDIO) return canvasProductPhotoWithoutBackgroundImageApiStage;
		return [];
	},
);

export const productPhotoWithoutBackgroundImageSelector = createSelector(
	[
		studioProductPhotoWithoutBackgroundImageSelector,
		canvasProductPhotoWithoutBackgroundImageSelector,
		(state, route) => route,
	],
	(studioProductPhotoWithoutBackgroundImage, canvasProductPhotoWithoutBackgroundImage, route) => {
		if (route === TEXT_TO_IMAGE) return studioProductPhotoWithoutBackgroundImage;
		if (route === STUDIO) return canvasProductPhotoWithoutBackgroundImage;
		return null;
	},
);

export const setProductPhotoWithoutBackgroundImageAsync = (link, id, route) => (dispatch) => {
	if (route === TEXT_TO_IMAGE) {
		dispatch(setStudioProductPhotoWithoutBackgroundImage({ id, url: link }));
	}
	if (route === STUDIO) {
		dispatch(setCanvasProductPhotoWithoutBackgroundImage({ id, url: link }));
	}
};

export const setProductPhotoCompositeImageAsync = (link, id, status, route) => (dispatch) => {
	if (route === TEXT_TO_IMAGE) {
		dispatch(setStudioProductPhotoCompositeImage({ id, url: link, status }));
	}
	if (route === STUDIO) {
		dispatch(setCanvasProductPhotoCompositeImage({ id, url: link, status }));
	}
};
export const productPhotoCompositeImageSelector = createSelector(
	[
		studioProductPhotoCompositeImageSelector,
		canvasProductPhotoCompositeImageSelector,
		(state, route) => route,
	],
	(studioProductPhotoCompositeImage, canvasProductPhotoCompositeImage, route) => {
		if (route === TEXT_TO_IMAGE) return studioProductPhotoCompositeImage;
		if (route === STUDIO) return canvasProductPhotoCompositeImage;
		return null;
	},
);

export const setProductPhotoObjectLocationImageAsync = (link, id, status, route) => (dispatch) => {
	if (route === TEXT_TO_IMAGE) {
		dispatch(setStudioProductPhotoObjectLocationImage({ id, url: link, status }));
	}
	if (route === STUDIO) {
		dispatch(setCanvasProductPhotoObjectLocationImage({ id, url: link, status }));
	}
};

export const productPhotoObjectLocationImageSelector = createSelector(
	[
		studioProductPhotoObjectLocationImageSelector,
		canvasProductPhotoObjectLocationImageSelector,
		(state, route) => route,
	],
	(studioProductPhotoObjectLocationImage, canvasProductPhotoObjectLocationImage, route) => {
		if (route === TEXT_TO_IMAGE) return studioProductPhotoObjectLocationImage;
		if (route === STUDIO) return canvasProductPhotoObjectLocationImage;
		return null;
	},
);

export const handleProductPhotoAssetFromPusherAsync = (newAsset) => (dispatch) => {
	dispatch(handleStudioProductPhotoAssetFromPusher(newAsset));
	dispatch(handleCanvasProductPhotoAssetFromPusher(newAsset));
};

export const setProductPhotoObjectTransformValues = (value, route) => (dispatch) => {
	if (route === TEXT_TO_IMAGE) {
		dispatch(setStudioProductPhotoObjectTransformValues(value));
	}
	if (route === STUDIO) {
		dispatch(setCanvasProductPhotoObjectTransformValues(value));
	}
};

export const imageReferenceStyleSelector = createSelector(
	[studioImageReferenceStyleSelector, canvasImageReferenceStyleSelector, (state, route) => route],
	(studioImageReferenceStyle, canvasImageReferenceStyle, route) => {
		if (route === TEXT_TO_IMAGE) return studioImageReferenceStyle;
		if (route === STUDIO) return canvasImageReferenceStyle;
		return [];
	},
);

export const imageReferenceStyleLinkAsync = (link, id, route) => (dispatch) => {
	if (route === TEXT_TO_IMAGE) {
		dispatch(setStudioImageReferenceStyle({ id, url: link }));
	}
	if (route === STUDIO) {
		dispatch(setCanvasImageReferenceStyle({ id, url: link }));
	}
};

export const resetImageReferenceStyleAsync = (route) => (dispatch) => {
	if (route === TEXT_TO_IMAGE) {
		dispatch(resetStudioImageReferenceStyle());
	}
	if (route === STUDIO) {
		dispatch(resetCanvasImageReferenceStyle());
	}
};

export const intensityCountInputSelector = createSelector(
	[studioIntensityCountInputSelector, canvasIntensityCountInputSelector, (state, route) => route],
	(studioIntensityCountInput, canvasIntensityCountInput, route) => {
		if (route === TEXT_TO_IMAGE) return studioIntensityCountInput;
		if (route === STUDIO) return canvasIntensityCountInput;
		return [];
	},
);
export const setIntensityCountInput = (value, route) => (dispatch) => {
	if (route === TEXT_TO_IMAGE) {
		dispatch(setStudioIntensityCountInput(value));
	} else if (route === STUDIO) {
		dispatch(setCanvasIntensityCountInput(value));
	}
};

export const productPhotoGuideDefaultStateSelector = createSelector(
	[
		studioProductPhotoImageToVideoGuideToggleSelector,
		canvasProductPhotoImageToVideoGuideToggleSelector,
		(state, route) => route,
	],
	(studioProductPhotoImageToVideoGuideToggle, canvasProductPhotoImageToVideoGuideToggle, route) => {
		if (route === TEXT_TO_IMAGE) return studioProductPhotoImageToVideoGuideToggle;
		if (route === STUDIO) return canvasProductPhotoImageToVideoGuideToggle;
		return null;
	},
);

export const setToggleProductPhotoImageToVideoGuide = (route, value) => (dispatch) => {
	if (route === TEXT_TO_IMAGE) {
		dispatch(toggleStudioProductPhotoImageToVideoGuide(value));
	}
	if (route === STUDIO) {
		dispatch(toggleCanvasProductPhotoImageToVideoGuide(value));
	}
};

export const videoAnyoneEndFrameImageSelector = createSelector(
	[
		studioVideoAnyoneEndFrameImageSelector,
		canvasVideoAnyoneEndFrameImageSelector,
		(state, route) => route,
	],
	(studioVideoAnyoneEndFrameImage, canvasVideoAnyoneEndFrameImage, route) => {
		if (route === TEXT_TO_IMAGE) return studioVideoAnyoneEndFrameImage;
		if (route === STUDIO) return canvasVideoAnyoneEndFrameImage;
		return [];
	},
);

export const videoAnyoneEndFrameImageLinkAsync = (link, id, route) => (dispatch) => {
	if (route === TEXT_TO_IMAGE) {
		dispatch(setStudioVideoAnyoneEndFrameImage({ id, url: link }));
	}
	if (route === STUDIO) {
		dispatch(setCanvasVideoAnyoneEndFrameImage({ id, url: link }));
	}
};

export const resetVideoAnyoneEndFrameImageAsync = (route) => (dispatch) => {
	if (route === TEXT_TO_IMAGE) {
		dispatch(resetStudioVideoAnyoneEndFrameImage());
	}
	if (route === STUDIO) {
		dispatch(resetCanvasVideoAnyoneEndFrameImage());
	}
};

export const videoAnyoneModelNameSelector = createSelector(
	[studioVideoAnyoneModelNameSelector, canvasVideoAnyoneModelNameSelector, (state, route) => route],
	(studioVideoAnyoneModelName, canvasVideoAnyoneModelName, route) => {
		if (route === TEXT_TO_IMAGE) return studioVideoAnyoneModelName;
		if (route === STUDIO) return canvasVideoAnyoneModelName;
		return [];
	},
);

export const setVideoAnyoneModelName = (value, route) => (dispatch) => {
	if (route === TEXT_TO_IMAGE) {
		dispatch(setStudioVideoAnyoneModelName(value));
	} else if (route === STUDIO) {
		dispatch(setCanvasVideoAnyoneModelName(value));
	}
};

export const setVideoAnyoneModelProvider = (value, route) => (dispatch) => {
	if (route === TEXT_TO_IMAGE) {
		dispatch(setStudioVideoAnyoneModelProvider(value));
	} else if (route === STUDIO) {
		dispatch(setCanvasVideoAnyoneModelProvider(value));
	}
};
